/**
 * Unit-agnostic consequence-value formatting — the ONE home for turning a smallest-unit integer + its
 * dimension metadata into a human string, shared by every surface that renders or edits an agent
 * consequence value (admin governor views, portal DSAR/approval views, the policy editor), so the same
 * value can never render two different ways across apps. Lives in auth-core (framework-agnostic, pure)
 * and is re-exported from `@pureid/ui` for existing importers.
 *
 * A value is an integer in the dimension's SMALLEST unit; `exponent` says where the decimal sits
 * (a 2-decimal currency ⇒ minor units/"cents"; a whole-count dimension ⇒ 0). A known ISO 4217 currency
 * (by `unitCode`) gets its symbol; anything else renders number + the dimension's own label — no
 * money-shaped assumptions, and no hardcoded "usd" anywhere.
 */

import { z } from "zod";

export interface DimMeta {
  displayName: string;
  exponent: number;
  unitCode?: string;
}

/**
 * The wire shape of a dimension's display metadata (snake_case), delivered keyed by dimension key in a
 * `dimensions` map on every response that renders consequence values. One shared schema so activity,
 * the agent detail, the simulator, and the consent card parse it identically — never a per-feature copy.
 */
export const dimMetaSchema = z.object({
  display_name: z.string(),
  exponent: z.number().int(),
  unit_code: z.string().optional(),
});
export type DimMetaWire = z.infer<typeof dimMetaSchema>;

/** Zod schema for a whole `dimensions` map (dimension key -> metadata). */
export const dimensionsWireSchema = z.record(z.string(), dimMetaSchema);

/** dimMetaFromWire adapts one snake_case wire entry to the DimMeta the formatters consume. */
export function dimMetaFromWire(w: DimMetaWire): DimMeta {
  return { displayName: w.display_name, exponent: w.exponent, unitCode: w.unit_code };
}

/** dimensionsMap builds a key -> DimMeta lookup from a parsed `dimensions` wire record. */
export function dimensionsMap(rec: Record<string, DimMetaWire> | undefined): Map<string, DimMeta> {
  const m = new Map<string, DimMeta>();
  if (rec) for (const [k, v] of Object.entries(rec)) m.set(k, dimMetaFromWire(v));
  return m;
}

// A few ISO 4217 currency symbols — a DISPLAY-only nicety keyed off the dimension's declared unit_code.
// An unlisted code falls back to the label form (no symbol invented).
const currencySymbols: Record<string, string> = {
  USD: "$", EUR: "€", GBP: "£", JPY: "¥", CNY: "¥", INR: "₹", CHF: "CHF", CAD: "$", AUD: "$",
  BRL: "R$", MXN: "$", SGD: "$", HKD: "$", SEK: "kr", NOK: "kr", ZAR: "R", AED: "د.إ", SAR: "﷼",
  KRW: "₩", BHD: ".د.ب", KWD: "د.ك",
};

/** currencySymbol returns the display symbol for a known ISO 4217 code, or undefined (no symbol invented). */
export function currencySymbol(unitCode?: string): string | undefined {
  return unitCode ? currencySymbols[unitCode.toUpperCase()] : undefined;
}

// Symbols shared by MORE THAN ONE catalog currency ($ = USD/CAD/AUD/…, ¥ = JPY/CNY, kr = SEK/NOK).
// For these the symbol alone is ambiguous, so formatValue rides the ISO code alongside it — an
// approver on a consent/approval surface must never mistake CAD $500 for USD $500.
const ambiguousSymbols: ReadonlySet<string> = (() => {
  const count: Record<string, number> = {};
  for (const code of Object.keys(currencySymbols)) {
    const s = currencySymbols[code];
    if (s) count[s] = (count[s] ?? 0) + 1;
  }
  return new Set(Object.keys(count).filter((s) => (count[s] ?? 0) > 1));
})();

/** formatQuantity shifts a smallest-unit integer by the dimension exponent and groups digits. */
export function formatQuantity(quantity: number, exponent: number): string {
  if (!Number.isFinite(quantity)) return String(quantity);
  if (exponent <= 0) return Math.round(quantity).toLocaleString("en-US");
  const scaled = quantity / 10 ** exponent;
  return scaled.toLocaleString("en-US", { minimumFractionDigits: exponent, maximumFractionDigits: exponent });
}

/**
 * formatValue renders a smallest-unit quantity through its dimension metadata: a known ISO currency
 * gets its symbol prefix ("$500.00"); anything else gets the number + the dimension label
 * ("50,000 records"). When meta is missing, falls back to the raw grouped integer + the key.
 */
export function formatValue(quantity: number, dimensionKey: string, meta?: DimMeta): string {
  if (!meta) return `${Math.round(quantity).toLocaleString("en-US")} ${dimensionKey}`;
  const num = formatQuantity(quantity, meta.exponent);
  const code = meta.unitCode ? meta.unitCode.toUpperCase() : undefined;
  const sym = code ? currencySymbols[code] : undefined;
  if (sym) {
    // A shared symbol ($, ¥, kr) can't identify the currency on its own — ride the ISO code so it's
    // unambiguous (CAD "$500.00 CAD" vs USD "$500.00 USD"); a unique symbol (€, £) stands alone.
    return ambiguousSymbols.has(sym) ? `${sym}${num} ${code}` : `${sym}${num}`;
  }
  // A currency whose symbol we don't know still shows its ISO code, not just a bare number.
  if (code) return `${num} ${code}`;
  return `${num} ${meta.displayName}`;
}
